import { useQuery } from 'react-query';

import { filter, uniqBy } from 'lodash-es';

import useTranslate from '../../../hooks/useTranslate';
import { get } from '../../http';
import { latinizeReplace } from '../../language/latinize';
import useGolfCourses from '../golfCourses/api';
import { MappedGolfCourseType } from '../golfCourses/types';
import { CountriesResponseType, MappedCountryType } from './types';

export const QUERY_KEY_COUNTRIES = 'countries';

export const getGolfCourseCountries = (
  golfCourses?: MappedGolfCourseType[]
): MappedCountryType[] => {
  if (!golfCourses) return [];

  return uniqBy(
    golfCourses.map(({ country }) => ({
      ...country,
      search: country.name.toLowerCase().replace(/[^A-Za-z0-9[\] ]/g, latinizeReplace),
    })),
    'id'
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(country => {
      const currentGoldCourses = golfCourses.filter(
        golfCourse => golfCourse.country.id === country.id
      );
      const latitude =
        currentGoldCourses.reduce((prev, curr) => prev + curr.latitude, 0) /
        currentGoldCourses.length;
      const longitude =
        currentGoldCourses.reduce((prev, curr) => prev + curr.longitude, 0) /
        currentGoldCourses.length;

      return {
        ...country,
        latitude,
        longitude,
      };
    });
};

export const useGolfCourseCountries = (courses?: MappedGolfCourseType[]) => {
  const { data: golfCourses } = useGolfCourses();

  return getGolfCourseCountries(courses ?? golfCourses);
};

// TODO api endpoint doesn't handle "lang" query parameter, so fallback to "language"
const getCountries = (params: { language: string }) =>
  get<CountriesResponseType>('v2/Countries', params);

export const useCountries = () => {
  const { activeLanguage } = useTranslate();

  return useQuery(QUERY_KEY_COUNTRIES, async () => {
    const countries = await getCountries({ language: activeLanguage.code });

    if (!countries) return null;

    return filter(uniqBy(countries, 'code'), 'code').sort((a, b) => a.name.localeCompare(b.name));
  });
};
